"use client";

import { useState, useEffect } from "react";
import { MapPin, Users, Calendar, ChevronRight } from "lucide-react";
import Link from "next/link";
import { IChapter } from "@/models/Chapter";

interface ChapterCardProps {
  chapter: IChapter;
}

const ChapterCard = ({ chapter }: ChapterCardProps) => {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 border-green-200";
      case "Inactive":
        return "bg-red-100 text-red-800 border-red-200";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "No Leader":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const generateSlug = (chapterName: string) => {
    return chapterName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <div className="bg-accent-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {chapter.name}
            </h3>
            <div className="flex items-center text-gray-600 mb-2">
              <MapPin className="h-4 w-4 mr-2" />
              <span className="text-sm">{chapter.chapterLocation}</span>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              chapter.status
            )}`}
          >
            {chapter.status || "Active"}
          </span>
        </div>

        {chapter.motto && (
          <p className="text-sm text-gray-600 italic mb-4 border-l-4 border-primary-500 pl-3">
            "{chapter.motto}"
          </p>
        )}

        {chapter.description && (
          <p className="text-gray-700 mb-4 line-clamp-3">
            {chapter.description}
          </p>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-gray-600">
            <Users className="h-4 w-4 mr-2" />
            <span className="text-sm">Chapter Members</span>
          </div>
          {chapter.leader && (
            <div className="text-sm text-gray-600">
              Led by: <span className="font-medium">{chapter.leader.name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center text-gray-500 text-xs">
            <Calendar className="h-3 w-3 mr-1" />
            {chapter.lastActivity
              ? new Date(chapter.lastActivity).toLocaleDateString()
              : new Date(chapter.createdAt!).toLocaleDateString()}
          </div>

          <Link
            href={`/chapter/${generateSlug(chapter.chapterName)}`}
            className="inline-flex items-center px-4 py-2 bg-primary-500 text-accent-200 text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            View Chapter
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const ChapterListingSkeleton = () => (
  <div className="bg-accent-200 rounded-lg shadow-md p-6 animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
      <div className="h-3 bg-gray-200 rounded w-24"></div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
  </div>
);

export default function ChaptersPage() {
  const [chapters, setChapters] = useState<IChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    fetchChapters();
  }, []);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      // We'll need to create a public API endpoint for this
      const response = await fetch("/api/chapters/public");
      if (!response.ok) {
        throw new Error("Failed to fetch chapters");
      }
      const data = await response.json();
      setChapters(data.chapters || []);
    } catch (err) {
      setError("Failed to load chapters. Please try again later.");
      console.error("Error fetching chapters:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredChapters = chapters.filter((chapter) => {
    const matchesSearch =
      chapter.chapterName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chapter.chapterLocation.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || chapter.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const activeChapters = chapters.filter((c) => c.status === "Active").length;
  const totalChapters = chapters.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-500 to-blue-800 text-accent-200">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              CONCES Chapters
            </h1>
            <p className="text-lg md:text-xl mb-8 text-blue-100">
              Discover our vibrant chapters across different locations and
              connect with fellow believers in your area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-blue-100">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                <span>{totalChapters} Total Chapters</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <span>{activeChapters} Active Chapters</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-accent-200 rounded-lg shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Search Chapters
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by chapter name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Filter by Status
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                  <option value="No Leader">No Leader</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters Grid */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <p className="text-red-600 text-center">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <ChapterListingSkeleton key={index} />
              ))}
            </div>
          ) : filteredChapters.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredChapters.map((chapter, index) => (
                <ChapterCard
                  key={String(chapter._id || chapter.id || index)}
                  chapter={chapter}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No chapters found
              </h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search criteria or filters."
                  : "There are no chapters available at the moment."}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
